import { LOOSE_PAGES_CHAPTER_ID, LOOSE_PAGES_NAME, type CompendiumChapter, type ReceivedVision } from '@sorrem/shared';
import type { UICompendium } from '@sorrem-client/types';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useMemo } from 'react';
import {
    type CharacterArgs,
    useCreateCompendiumChapterMutation,
    useDeleteCompendiumChapterMutation,
    useGetCharacterVisionsQuery,
    useGetCompendiumChaptersQuery,
    useAddCompendiumChapterVisionMutation,
    useRemoveCompendiumChapterVisionMutation,
    useSetCompendiumChapterVisionsMutation,
    useUpdateCompendiumChapterMutation,
} from '@sorrem-client/ui/store';
import { useCurrentCharacterArgs } from './use-current-character-id';
import { useMutationError } from './use-mutation-error';

const NO_VISIONS: readonly ReceivedVision[] = [];
const NO_CHAPTERS: readonly CompendiumChapter[] = [];

export interface CompendiumState {
    /** The chapters in order, each with its visions, the loose pages last. */
    compendium: UICompendium;
    /** Whether both the visions and the chapters are fresh from the server. */
    isLoaded: boolean;
    /** Whether a change is on its way to the server. */
    isSaving: boolean;
    /** Why the last change failed, or null. */
    error: string | null;
    /** Adds a chapter after the others. Resolves to its id, or null if it failed. */
    createChapter: (name: string) => Promise<string | null>;
    renameChapter: (chapterId: string, name: string) => Promise<boolean>;
    /** Moves a chapter to a 0-based index among the chapters. */
    moveChapter: (chapterId: string, position: number) => Promise<boolean>;
    /** Removes a chapter; its visions go to the end of the loose pages. */
    deleteChapter: (chapterId: string) => Promise<boolean>;
    /** Sets a chapter's visions, in order; any it held but leaves out go to (or, for the loose pages, stay at the end of) the loose pages. */
    setChapterVisions: (chapterId: string, visionIds: readonly string[]) => Promise<boolean>;
    /** Adds a vision to a chapter at a 0-based index (the end when omitted), or moves it within one. */
    addVisionToChapter: (chapterId: string, visionId: string, position?: number) => Promise<boolean>;
    /** Returns a vision from its chapter to the end of the loose pages (not for the loose pages themselves). */
    removeVisionFromChapter: (chapterId: string, visionId: string) => Promise<boolean>;
}

/**
 * The tome's view of a Compendium: each chapter with its visions in order,
 * the loose pages last. The loose pages are always there, even before the
 * chapters have loaded, and take any vision naming a chapter that isn't
 * listed (removed a moment ago).
 */
export function buildCompendiumView(chapters: readonly CompendiumChapter[], visions: readonly ReceivedVision[]): UICompendium {
    const ordered = [
        ...chapters.filter((chapter) => !chapter.isLoose),
        chapters.find((chapter) => chapter.isLoose) ?? { id: LOOSE_PAGES_CHAPTER_ID, name: LOOSE_PAGES_NAME, isLoose: true },
    ];
    const chapterIds = new Set(ordered.map((chapter) => chapter.id));
    const placeOf = (vision: ReceivedVision): string => (chapterIds.has(vision.chapterId) ? vision.chapterId : LOOSE_PAGES_CHAPTER_ID);
    return {
        chapters: ordered.map((chapter) => ({
            ...chapter,
            visions: visions.filter((vision) => placeOf(vision) === chapter.id).sort((a, b) => a.position - b.position),
        })),
    };
}

/** The current character's Compendium, and the player's ways of arranging it. */
export function useCompendium(): CompendiumState {
    const args = useCurrentCharacterArgs();
    const visionsQuery = useGetCharacterVisionsQuery(args, { refetchOnMountOrArgChange: true });
    const chaptersQuery = useGetCompendiumChaptersQuery(args, { refetchOnMountOrArgChange: true });
    const [createChapterMutation, { isLoading: isCreating }] = useCreateCompendiumChapterMutation();
    const [updateChapterMutation, { isLoading: isUpdating }] = useUpdateCompendiumChapterMutation();
    const [deleteChapterMutation, { isLoading: isDeleting }] = useDeleteCompendiumChapterMutation();
    const [setChapterVisionsMutation, { isLoading: isSetting }] = useSetCompendiumChapterVisionsMutation();
    const [addChapterVisionMutation, { isLoading: isAdding }] = useAddCompendiumChapterVisionMutation();
    const [removeChapterVisionMutation, { isLoading: isRemoving }] = useRemoveCompendiumChapterVisionMutation();
    const { error, refuse, run } = useMutationError();

    const visions = visionsQuery.data ?? NO_VISIONS;
    const chapters = chaptersQuery.data ?? NO_CHAPTERS;
    const compendium = useMemo(() => buildCompendiumView(chapters, visions), [chapters, visions]);

    /** Runs one change for the current character, recording why it failed. Resolves to whether it succeeded. */
    const succeeded = useCallback((change: (characterArgs: CharacterArgs) => Promise<unknown>): Promise<boolean> => (
        args === skipToken
            ? refuse('You need to be signed in to change your Compendium.')
            : run(() => change(args), 'Your Compendium couldn\'t be changed. Try again.')
    ), [args, refuse, run]);

    const createChapter = useCallback(async (name: string): Promise<string | null> => {
        let chapterId: string | null = null;
        await succeeded(async (characterArgs) => {
            chapterId = (await createChapterMutation({ ...characterArgs, body: { name } }).unwrap()).id;
        });
        return chapterId;
    }, [createChapterMutation, succeeded]);

    const renameChapter = useCallback((chapterId: string, name: string): Promise<boolean> => (
        succeeded((characterArgs) => updateChapterMutation({ ...characterArgs, chapterId, body: { name } }).unwrap())
    ), [succeeded, updateChapterMutation]);

    const moveChapter = useCallback((chapterId: string, position: number): Promise<boolean> => (
        succeeded((characterArgs) => updateChapterMutation({ ...characterArgs, chapterId, body: { position } }).unwrap())
    ), [succeeded, updateChapterMutation]);

    const deleteChapter = useCallback((chapterId: string): Promise<boolean> => (
        succeeded((characterArgs) => deleteChapterMutation({ ...characterArgs, chapterId }).unwrap())
    ), [deleteChapterMutation, succeeded]);

    const setChapterVisions = useCallback((chapterId: string, visionIds: readonly string[]): Promise<boolean> => (
        succeeded((characterArgs) => setChapterVisionsMutation({ ...characterArgs, chapterId, body: { visionIds: [...visionIds] } }).unwrap())
    ), [setChapterVisionsMutation, succeeded]);

    const addVisionToChapter = useCallback((chapterId: string, visionId: string, position?: number): Promise<boolean> => (
        succeeded((characterArgs) => addChapterVisionMutation({ ...characterArgs, chapterId, visionId, body: position === undefined ? {} : { position } }).unwrap())
    ), [addChapterVisionMutation, succeeded]);

    const removeVisionFromChapter = useCallback((chapterId: string, visionId: string): Promise<boolean> => (
        succeeded((characterArgs) => removeChapterVisionMutation({ ...characterArgs, chapterId, visionId }).unwrap())
    ), [removeChapterVisionMutation, succeeded]);

    return {
        compendium,
        isLoaded: visionsQuery.isSuccess && !visionsQuery.isFetching && chaptersQuery.isSuccess && !chaptersQuery.isFetching,
        isSaving: isCreating || isUpdating || isDeleting || isSetting || isAdding || isRemoving,
        error,
        createChapter,
        renameChapter,
        moveChapter,
        deleteChapter,
        setChapterVisions,
        addVisionToChapter,
        removeVisionFromChapter,
    };
}
